/**
 * AI Recruit - Authentication Hook
 * Custom hook for authentication state and operations
 */

import { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import type { RootState } from '../stores'
import { 
  loginStart, 
  loginSuccess, 
  loginFailure, 
  logout as logoutAction, 
  setLoading,
  clearError
} from '../stores/auth/authSlice'
import { authApi } from '../services/api'

// Global flags to prevent multiple simultaneous auth checks
let authCheckInProgress = false
let authCheckCompleted = false

// Transform backend user data to frontend User interface
const transformUserData = (backendUser: any) => ({
  id: backendUser.id,
  email: backendUser.email,
  username: backendUser.username,
  firstName: backendUser.first_name || '',
  lastName: backendUser.last_name || '',
  fullName: `${backendUser.first_name || ''} ${backendUser.last_name || ''}`.trim(),
  role: backendUser.role || 'user',
  organizationId: backendUser.organization_id || 'default_org',
  avatar: backendUser.avatar || '',
  permissions: backendUser.permissions || []
})

export const useAuth = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { user, token, isAuthenticated, isLoading, error } = useSelector(
    (state: RootState) => state.auth
  )
  
  // Note: Using global flags instead of useRef to prevent multiple auth checks across component instances

  // Check authentication status on app load
  useEffect(() => {
    // Prevent multiple auth checks globally - use global flags instead of ref
    if (authCheckCompleted || authCheckInProgress) {
      console.log('useAuth: Auth check already completed or in progress, skipping') // Debug log
      return
    }
    
    // Also skip if user is already authenticated (prevents re-checking after login)
    if (isAuthenticated && user) {
      console.log('useAuth: User already authenticated, skipping auth check') // Debug log
      authCheckCompleted = true
      return
    }
    
    const checkAuth = async () => {
      const savedToken = localStorage.getItem('ai_recruit_token')
      
      console.log('useAuth: Starting auth check, token exists:', !!savedToken) // Debug log
      authCheckInProgress = true
      
      if (savedToken) {
        try {
          dispatch(setLoading(true))
          console.log('useAuth: Calling getCurrentUser API') // Debug log
          const response = await authApi.getCurrentUser()
          
          console.log('useAuth: getCurrentUser success') // Debug log
          dispatch(loginSuccess({
            user: transformUserData(response.data),
            token: savedToken
          }))
        } catch (error) {
          console.log('useAuth: getCurrentUser failed, clearing tokens') // Debug log
          // Token is invalid, remove it
          localStorage.removeItem('ai_recruit_token')
          localStorage.removeItem('ai_recruit_refresh_token')
          dispatch(logoutAction())
        }
      } else {
        console.log('useAuth: No token found, setting loading false') // Debug log
        dispatch(setLoading(false))
      }
      
      // Mark auth check as completed globally
      authCheckInProgress = false
      authCheckCompleted = true
    }

    // Only run once on mount
    checkAuth()
  }, []) // Empty dependency array

  const login = async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      dispatch(loginStart())
      
      const response = await authApi.login({ email, password, remember_me: rememberMe })
      
      // Store refresh token
      localStorage.setItem('ai_recruit_refresh_token', response.data.refresh_token)
      
      dispatch(loginSuccess({
        user: transformUserData(response.data.user),
        token: response.data.access_token
      }))
      
      return { success: true }
    } catch (error: any) {
      console.log('Login error:', error) // Debug log
      const errorMessage = error.response?.data?.detail || 'Login failed'
      dispatch(loginFailure(errorMessage))
      return { success: false, error: errorMessage }
    }
  }

  const logout = async (revokeAllSessions: boolean = false) => {
    try {
      await authApi.logout(revokeAllSessions)
    } catch (error) {
      // Continue with logout even if API call fails
      console.warn('Logout API call failed:', error)
    } finally {
      // Clear refresh token
      localStorage.removeItem('ai_recruit_refresh_token')
      dispatch(logoutAction())

      // Purge the entire React Query cache — it isn't scoped per-user
      // (e.g. usePermission()'s `['my-capabilities']`), so without this a
      // logout→login within the same tab (no full page reload) serves the
      // PREVIOUS user's cached data until it naturally goes stale. Found
      // live 2026-08-16: an admin lost their own "Organization Settings"
      // link after a manager account had just been logged into and out of
      // in the same tab, until a hard refresh (or several minutes) passed.
      queryClient.clear()

      // Reset global auth flags to allow new login
      authCheckCompleted = false
      authCheckInProgress = false
    }
  }

  const register = async (userData: {
    email: string
    password: string
    confirmPassword: string
    firstName: string
    lastName: string
    username?: string
    phone?: string
    organizationName?: string
    organizationDomain?: string
    organizationIndustry?: string
  }) => {
    try {
      dispatch(loginStart())
      
      const response = await authApi.register({
        email: userData.email,
        password: userData.password,
        confirm_password: userData.confirmPassword,
        first_name: userData.firstName,
        last_name: userData.lastName,
        username: userData.username,
        phone: userData.phone,
        organization_name: userData.organizationName,
        organization_domain: userData.organizationDomain,
        organization_industry: userData.organizationIndustry
      })
      
      // Store refresh token
      localStorage.setItem('ai_recruit_refresh_token', response.data.refresh_token)
      
      dispatch(loginSuccess({
        user: transformUserData(response.data.user),
        token: response.data.access_token
      }))
      
      return { success: true }
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Registration failed'
      dispatch(loginFailure(errorMessage))
      return { success: false, error: errorMessage }
    }
  }

  const refreshToken = async () => {
    try {
      const savedRefreshToken = localStorage.getItem('ai_recruit_refresh_token')
      if (!savedRefreshToken) {
        throw new Error('No refresh token available')
      }

      const response = await authApi.refreshToken(savedRefreshToken)
      
      // Store new refresh token
      localStorage.setItem('ai_recruit_refresh_token', response.data.refresh_token)
      
      dispatch(loginSuccess({
        user: transformUserData(response.data.user),
        token: response.data.access_token
      }))
      
      return { success: true }
    } catch (error) {
      // Clear tokens and logout
      localStorage.removeItem('ai_recruit_refresh_token')
      dispatch(logoutAction())
      return { success: false }
    }
  }

  const clearAuthError = () => {
    dispatch(clearError())
  }

  return {
    user,
    token,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    register,
    refreshToken,
    clearError: clearAuthError,
  }
}